/**
 * PPS (Package Price Service) API Schemas
 *
 * Zod schemas for PPS GetPackagePricesAndAvailabilitiesV2 request and response.
 * Field names match the actual Bewotec PPS API response structure.
 *
 * KEY DIFFERENCE from SSC:
 * - PPS returns the full package (hotel + extras) as nested Services[]
 * - We filter out the main hotel service (Requirement: "H" / OrderInPackage: 0)
 * - Only keep extras (Requirement: "SO", "BU", etc. / OrderInPackage > 0)
 */
import { z } from 'zod';
import { parseAuxiliaryAvailability } from '@/constants/extras/availability';

// ── Request Schemas ────────────────────────────────────────────────────────

export const PpsScheduleSchema = z.object({
  From: z.string(), // /Date(ms)/ .NET format
  To: z.string(),
});

export const PpsSearchCriteriaSchema = z.object({
  CatalogCode: z.string(),
  Agency: z.number(),
  Durations: z.array(z.number()),
  ManualSchedules: z.object({
    Schedules: z.array(PpsScheduleSchema),
  }),
});

export const PpsPackageSearchCriteriaSchema = z.object({
  PackageBookingCode: z.string(),
  Adults: z.number(),
  ComponentsToInclude: z.number(),
  IgnorePackageServiceTypes: z.boolean(),
  UsePackageDatesForVariableServices: z.boolean(),
  IgnoreMaxOccupancyForAccommodations: z.boolean(),
});

export const GetExtrasRequestSchema = z.object({
  Login: z.object({}),
  SearchCriteria: PpsSearchCriteriaSchema,
  PackageSearchCriteria: z.array(PpsPackageSearchCriteriaSchema),
  ShowNotAvailable: z.boolean(),
});

// ── Response Schemas ───────────────────────────────────────────────────────

export const PpsPricePerParticipantSchema = z.object({
  ParticipantIndex: z.number(),
  Price: z.number(),
  Type: z.number(),
});

export const PpsDepositInfoSchema = z.object({
  Percentage: z.number().optional(),
  TypeOfDeposit: z.number().optional(),
});

export const PpsServiceTypeSchema = z.object({
  Code: z.string(),
  Code_Name: z.string(),
  Participants: z.number().optional(),
  Availability: z.string(), // "*" = available, "R" = on request
  Price: z.number(), // price per person
  Total: z.number(), // total price for all participants
  Currency: z.string(),
  Adults: z.number().optional(),
  MinimumOccupation: z.number().optional(),
  MaximumOccupation: z.number().optional(),
  MinimumAdults: z.number().optional(),
  MaximumAdults: z.number().optional(),
  MaximumChilds: z.number().optional(),
  MaximumBabys: z.number().optional(),
  DetailedAvailability: z.string().optional(),
  PricesPerParticipant: z.array(PpsPricePerParticipantSchema).optional(),
  ServiceTypesFromPackageViaMatrix: z.array(z.string()).optional(),
  DepositInfos: z.array(PpsDepositInfoSchema).optional(),
  AvailabilityErrorInfo: z.unknown().nullable().optional(),
  OnlyInCombinationWith: z.record(z.unknown()).optional(),
});

export const PpsServiceSchema = z.object({
  __type: z.string().optional(), // "PackageOtherService:...", "PackageBus:...", etc.
  Requirement: z.string(), // "H" = Hotel (skip), "SO" = Special Order, "BU" = Bus, "P" = Package
  OrderInPackage: z.number(),
  BookingCode: z.string(),
  BookingCodeName: z.string(),
  BookingCodeId: z.number(),
  RequirementFlag: z.number().optional(),
  TypeOfAssignment: z.number().optional(),
  PackageGroupName: z.string().optional(), // Groups related services for mutual exclusivity
  From: z.string().optional(),
  To: z.string().optional(),
  ServiceTypes: z.array(PpsServiceTypeSchema).optional(),
  CatalogueName: z.string().optional(),
});

export const PpsServiceGroupSchema = z.object({
  GroupName: z.string().optional(),
  Services: z.array(PpsServiceSchema),
});

export const PpsOccupancyBoardSchema = z.object({
  OccupancyBoard: z.string().optional(),
  ServiceGroups: z.array(PpsServiceGroupSchema),
});

export const PpsScheduleResponseSchema = z.object({
  From: z.string().optional(),
  To: z.string().optional(),
  Bookability: z.number().optional(),
  OccupancyBoards: z.array(PpsOccupancyBoardSchema),
});

export const PpsPackageSchema = z.object({
  PackageBookingCode: z.string(),
  PackageRequirement: z.string().optional(),
  Schedules: z.array(PpsScheduleResponseSchema),
});

export const GetExtrasResponseSchema = z.object({
  Packages: z.array(PpsPackageSchema),
});

// ── Types ──────────────────────────────────────────────────────────────────

export type PpsSchedule = z.infer<typeof PpsScheduleSchema>;
export type PpsSearchCriteria = z.infer<typeof PpsSearchCriteriaSchema>;
export type PpsPackageSearchCriteria = z.infer<typeof PpsPackageSearchCriteriaSchema>;
export type GetExtrasRequest = z.infer<typeof GetExtrasRequestSchema>;
export type PpsPricePerParticipant = z.infer<typeof PpsPricePerParticipantSchema>;
export type PpsServiceType = z.infer<typeof PpsServiceTypeSchema>;
export type PpsService = z.infer<typeof PpsServiceSchema>;
export type PpsServiceGroup = z.infer<typeof PpsServiceGroupSchema>;
export type PpsOccupancyBoard = z.infer<typeof PpsOccupancyBoardSchema>;
export type PpsScheduleResponse = z.infer<typeof PpsScheduleResponseSchema>;
export type PpsPackage = z.infer<typeof PpsPackageSchema>;
export type GetExtrasResponse = z.infer<typeof GetExtrasResponseSchema>;

// ── Helpers ────────────────────────────────────────────────────────────────

/**
 * Check if a PPS service is an "extra" (not the main hotel service).
 * Main hotel service has Requirement: "H" and OrderInPackage: 0.
 */
export function isExtra(service: PpsService): boolean {
  return service.Requirement !== 'H' && service.OrderInPackage > 0;
}

/**
 * Extract all extras from a PPS response.
 * Navigates the deep nesting: Packages[0].Schedules[0].OccupancyBoards[0].ServiceGroups[*].Services[*]
 * Filters to only extras (non-hotel services).
 */
export function extractExtrasFromResponse(response: GetExtrasResponse): PpsService[] {
  if (response.Packages.length === 0) return [];

  const pkg = response.Packages[0];
  if (pkg.Schedules.length === 0) return [];

  const schedule = pkg.Schedules[0];
  if (schedule.OccupancyBoards.length === 0) return [];

  const board = schedule.OccupancyBoards[0];
  const extras: PpsService[] = [];

  for (const group of board.ServiceGroups) {
    for (const service of group.Services) {
      if (!isExtra(service)) continue;
      if (typeof service.BookingCodeId !== 'number') {
        console.warn(
          `[PPS] Skipping extra without BookingCodeId: ${service.BookingCode} (${service.BookingCodeName})`
        );
        continue;
      }
      extras.push(service);
    }
  }

  return extras;
}

/** Check if response has any extras */
export function hasExtras(response: GetExtrasResponse): boolean {
  return extractExtrasFromResponse(response).length > 0;
}

/** Readonly-compatible extra type for helper functions */
type ReadonlyPpsService = {
  readonly ServiceTypes?: ReadonlyArray<{
    readonly Price: number;
    readonly Total: number;
    readonly Availability: string;
  }>;
};

/**
 * Get the total price for an extra service (uses first ServiceType).
 * PPS API returns Price (per person) and Total (for all participants) in ServiceTypes[0].
 * In practice Total is always 0 (even with multiple participants), so we use `||`
 * to fall back to Price. We keep Total as primary in case the API starts populating it.
 */
export function getExtraTotalPrice(extra: ReadonlyPpsService): number {
  if (!extra.ServiceTypes?.length) return 0;
  const st = extra.ServiceTypes[0];
  return st.Total || st.Price;
}

/**
 * Get the availability status of an extra service (uses first ServiceType).
 * `'hidden'` is degraded to `'unavailable'` for the display layer; filtering
 * out hidden items happens in the composable that builds the display list.
 */
export function getExtraAvailability(
  extra: ReadonlyPpsService
): 'available' | 'on-request' | 'unavailable' {
  if (!extra.ServiceTypes?.length) return 'unavailable';
  const state = parseAuxiliaryAvailability(extra.ServiceTypes[0].Availability);
  return state === 'hidden' ? 'unavailable' : state;
}

/** Readonly-compatible service type for helper functions */
type ReadonlyPpsServiceType = {
  readonly Price: number;
  readonly Total: number;
  readonly Availability: string;
};

/**
 * Get total price for a specific ServiceType.
 * Total is currently always 0 from the API — we use `||` to fall back to Price.
 * Keeping Total as primary in case the API starts populating it for multi-participant scenarios.
 */
export function getServiceTypeTotalPrice(st: ReadonlyPpsServiceType): number {
  return st.Total || st.Price;
}

/**
 * Get availability status for a specific ServiceType.
 * `'hidden'` is degraded to `'unavailable'` for the display layer; filtering
 * out hidden items happens in `transportPairing.buildExtrasGroups`.
 */
export function getServiceTypeAvailability(
  st: ReadonlyPpsServiceType
): 'available' | 'on-request' | 'unavailable' {
  const state = parseAuxiliaryAvailability(st.Availability);
  return state === 'hidden' ? 'unavailable' : state;
}
